import type { OfflineAction } from "./actions";
import { OutboxDispatcher } from "./dispatcher";
import { type EnqueueOptions, OutboxStore, emptySnapshot } from "./store";
import type { OutboxItem, OutboxSnapshot } from "./types";

interface Outbox {
  store: OutboxStore;
  dispatcher: OutboxDispatcher;
}

let outbox: Outbox | null = null;
let opening: Promise<Outbox> | null = null;
let unsubscribeStore: (() => void) | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function initOutbox(): Promise<Outbox> {
  if (outbox) {
    return Promise.resolve(outbox);
  }
  if (!opening) {
    opening = OutboxStore.open().then((store) => {
      const dispatcher = new OutboxDispatcher({ store });
      const created: Outbox = { store, dispatcher };
      unsubscribeStore = store.subscribe(notify);
      outbox = created;
      dispatcher.start();
      notify();
      return created;
    });
  }
  return opening;
}

export async function claimOutbox(ownerKey: string): Promise<void> {
  const { store, dispatcher } = await initOutbox();
  if (store.currentOwnerKey() === ownerKey) {
    return;
  }
  await store.claim(ownerKey);
  void dispatcher.run();
}

export function releaseOutbox(): void {
  outbox?.store.release();
}

export function resetOutbox(): void {
  outbox?.dispatcher.stop();
  outbox?.store.close();
  unsubscribeStore?.();
  unsubscribeStore = null;
  outbox = null;
  opening = null;
  notify();
}

export function subscribeToOutbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function outboxSnapshot(): OutboxSnapshot {
  return outbox ? outbox.store.getSnapshot() : emptySnapshot();
}

export async function enqueueAction(
  action: OfflineAction,
  options?: EnqueueOptions,
): Promise<OutboxItem> {
  const { store, dispatcher } = await initOutbox();
  const item = await store.enqueue(action, options);
  void dispatcher.run();
  return item;
}

export async function retryOutboxItem(id: string): Promise<void> {
  const { store, dispatcher } = await initOutbox();
  await store.retry(id);
  void dispatcher.run();
}

export async function discardOutboxItem(id: string): Promise<void> {
  const { store } = await initOutbox();
  await store.discard(id);
}

export async function flushOutbox(): Promise<void> {
  const { dispatcher } = await initOutbox();
  await dispatcher.run();
}
